"use client";

export type TextSize = "normal" | "large" | "xlarge";
export type Look = "normal" | "washi" | "parchment" | "slate" | "night" | "game" | "digital" | "cute" | "elegant" | "forest";

/** メインの画面の見え方。キャラごとにブラウザへ覚える */
export interface ViewSetting {
  size: TextSize;
  look: Look;
}

export const VIEW_DEFAULT: ViewSetting = { size: "normal", look: "normal" };

const SIZES: { value: TextSize; label: string }[] = [
  { value: "normal", label: "普通" },
  { value: "large", label: "大" },
  { value: "xlarge", label: "特大" },
];

const LOOKS: { value: Look; label: string; note: string }[] = [
  { value: "normal", label: "通常", note: "濃紺の地に明朝" },
  { value: "washi", label: "和紙", note: "生成りの紙に筆文字" },
  { value: "parchment", label: "羊皮紙", note: "古い紙に焦げ茶の明朝" },
  { value: "slate", label: "石板", note: "灰の石に刻んだ字" },
  { value: "night", label: "夜空", note: "星の夜に光る明朝" },
  { value: "game", label: "ゲーム", note: "黒の地に白のドット文字" },
  { value: "digital", label: "デジタル", note: "回路の地に光る等幅の字" },
  { value: "cute", label: "かわいい", note: "桃色の地に丸い字" },
  { value: "elegant", label: "エレガント", note: "葡萄酒色の織りに金の明朝" },
  { value: "forest", label: "森", note: "深緑の葉陰にペンの字" },
];

export function viewKey(accountId: string, world: string, region: string, slug: string): string {
  return `rakuen-play-view:${accountId}:${world}:${region}:${slug}`;
}

export function readView(key: string): ViewSetting {
  try {
    const saved = JSON.parse(window.localStorage.getItem(key) ?? "null") as Partial<ViewSetting> | null;
    return {
      size: SIZES.some((one) => one.value === saved?.size) ? (saved?.size as TextSize) : VIEW_DEFAULT.size,
      look: LOOKS.some((one) => one.value === saved?.look) ? (saved?.look as Look) : VIEW_DEFAULT.look,
    };
  } catch {
    return VIEW_DEFAULT;
  }
}

export function writeView(key: string, value: ViewSetting): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* 保存できなくても動く */
  }
}

/** 設定画面。メインの画面の文字サイズと表示を選ぶ。選ぶたびにその場で覚える */
export default function PlaySettings({ value, onChange }: { value: ViewSetting; onChange: (next: ViewSetting) => void }) {
  return (
    <div>
      <section className="pl-prof-sec">
        <h3>文字サイズ</h3>
        <div className="pl-seg pl-set-seg3" role="radiogroup" aria-label="文字サイズ">
          {SIZES.map((one) => (
            <button
              key={one.value}
              type="button"
              role="radio"
              aria-checked={value.size === one.value}
              className={value.size === one.value ? "pl-seg-on" : ""}
              onClick={() => onChange({ ...value, size: one.value })}
            >
              {one.label}
            </button>
          ))}
        </div>
      </section>
      <section className="pl-prof-sec">
        <h3>表示</h3>
        <div className="pl-set-looks" role="radiogroup" aria-label="表示">
          {LOOKS.map((one) => (
            <button
              key={one.value}
              type="button"
              role="radio"
              aria-checked={value.look === one.value}
              className={`pl-set-look pl-look-${one.value}${value.look === one.value ? " pl-set-look-on" : ""}`}
              onClick={() => onChange({ ...value, look: one.value })}
            >
              <b>{one.label}</b>
              <small>{one.note}</small>
            </button>
          ))}
        </div>
      </section>
      <section className="pl-prof-sec">
        <h3>見本</h3>
        <div className={`pl-set-sample pl-look-${value.look} pl-size-${value.size}`}>
          <div className="pl-story">
            <p>ここに物語の散文が並びます。</p>
            <p>文字サイズと表示を選ぶと、この見本が切り替わります。</p>
          </div>
        </div>
      </section>
    </div>
  );
}
